import moment from 'moment-timezone';

import { formatFullName } from '@/helpers/name';
import i18n from '@/i18n';
import { useAuthStore } from '@/modules/auth/store/authStore';
import { useConfigurationStore } from '@/store/configurationStore';

import type { FollowingEvent } from '@/components/following/helpers/sectionHelper';

import {
	buildFollowingLinePriseDeContact,
	PriseDeContactType,
} from '@/components/following/helpers/contactTypeFactory';
import { buildFollowingLineInterviewReport } from '@/components/following/helpers/interviewReportFactory';
import { buildFollowingLineNote } from '@/components/following/helpers/noteFactory';
import {
	buildFollowingLineProfil,
	ProfilFollowingType,
} from '@/components/following/helpers/profilFactory';
import { createReadonlyLabelSegment } from '@/components/following/helpers/segmentFactory';
import {
	type FollowingLineGeneratorResult,
	type ProfileHistoryItem,
	type ProfileHistoryLoggerPayload,
	WorkerActivitySource,
} from '@/components/following/types';

const PROFILE_HISTORY_ADD_CV_TYPE = 'add-cv';
const PROFILE_HISTORY_DATA_UPDATED_TYPE = 'dataUpdated';
const PROFILE_HISTORY_INTERVIEW_REPORT_TYPES = new Set([
	'interview_report_created',
	'interview',
]);
const PROFILE_HISTORY_WORKER_CONTACTED_TYPE = 'worker_contacted';
const PROFILE_HISTORY_WORKER_CREATED_TYPE = 'worker_created';
const PROFILE_HISTORY_WORKER_CREATED_FROM_MOBILE_TYPE =
	'worker_created_from_mobile';
const PROFILE_HISTORY_WORKER_CREATED_FROM_JOBBOARD_TYPE =
	'worker_created_from_jobboard';
const PROFILE_HISTORY_CREATION_ICON = 'user-plus';

interface WorkerContactedPayload extends ProfileHistoryLoggerPayload {
	contactType?: string;
	comment?: string;
}

interface WorkerCreatedPayload extends ProfileHistoryLoggerPayload {
	source?: string;
	originOfApplication?: string;
	jobboardSource?: string;
}

const normalizeProfileHistoryType = (type: string | undefined): string =>
	type?.trim().toLowerCase() ?? '';

const isProfileHistoryCreationType = (type: string | undefined): boolean => {
	const normalized = normalizeProfileHistoryType(type);
	return (
		normalized === PROFILE_HISTORY_WORKER_CREATED_TYPE ||
		normalized === PROFILE_HISTORY_WORKER_CREATED_FROM_MOBILE_TYPE ||
		normalized === PROFILE_HISTORY_WORKER_CREATED_FROM_JOBBOARD_TYPE
	);
};

/** Evolia imports reuse the `worker_created` type; the payload discriminates them from a standard back-office creation. */
const isEvoliaImportCreation = (payload: WorkerCreatedPayload): boolean =>
	payload.source?.trim().toUpperCase() === 'IMPORT' &&
	payload.originOfApplication?.trim().toUpperCase() === 'EVOLIA';

/** Only a back-office `worker_created` carries an author; mobile, jobboard and legacy creations have none. */
const hasResolvableCreationAuthor = (item: ProfileHistoryItem): boolean =>
	normalizeProfileHistoryType(item.type) ===
		PROFILE_HISTORY_WORKER_CREATED_TYPE && Boolean(item.collaboratorId?.trim());

const resolveCreationLabelKey = (
	item: ProfileHistoryItem,
	payload: WorkerCreatedPayload
): string => {
	const normalized = normalizeProfileHistoryType(item.type);
	if (normalized === PROFILE_HISTORY_WORKER_CREATED_FROM_MOBILE_TYPE) {
		return 'following.labels.profileHistoryCreation.fromMobile';
	}
	if (normalized === PROFILE_HISTORY_WORKER_CREATED_FROM_JOBBOARD_TYPE) {
		return payload.jobboardSource?.trim()
			? 'following.labels.profileHistoryCreation.fromJobboard'
			: 'following.labels.profileHistoryCreation.fromJobboardUnknown';
	}
	if (isEvoliaImportCreation(payload)) {
		return 'following.labels.profileHistoryCreation.fromEvolia';
	}
	return 'following.labels.profileHistoryCreation.manual';
};

const resolveCreationLabel = (
	item: ProfileHistoryItem,
	payload: WorkerCreatedPayload
): string => {
	const key = resolveCreationLabelKey(item, payload);
	if (key === 'following.labels.profileHistoryCreation.fromJobboard') {
		// biome-ignore lint/style/noNonNullAssertion: Biome Init
		return i18n.global.t(key, { jobboard: payload.jobboardSource!.trim() });
	}
	return i18n.global.t(key);
};

export const isWorkerContactedProfileHistoryType = (
	type: string | undefined
): boolean =>
	type?.trim().toLowerCase() === PROFILE_HISTORY_WORKER_CONTACTED_TYPE;

/** API uses `EMAIL` while the legacy following factory enum uses `MAIL`; align here without forking the factory. */
const WORKER_CONTACTED_TYPE_ALIASES: Record<string, PriseDeContactType> = {
	EMAIL: PriseDeContactType.MAIL,
};

const normalizeWorkerContactedType = (
	value: string | undefined
): PriseDeContactType | undefined => {
	if (value == null) return undefined;
	const upper = value.trim().toUpperCase();
	if (!upper) return undefined;
	if ((Object.values(PriseDeContactType) as string[]).includes(upper)) {
		return upper as PriseDeContactType;
	}
	return WORKER_CONTACTED_TYPE_ALIASES[upper];
};

/** Logger `type` values from worker profile history (API) mapped to line icon and i18n label. */
const PROFILE_HISTORY_LOGGER_TYPE_CONFIG: Record<
	string,
	{ icon: string; labelKey: string }
> = {
	worker_cv_path_updated: {
		icon: 'doc-cv',
		labelKey: 'following.labels.profileHistoryLogger.workerCvPathUpdated',
	},
	worker_updated: {
		icon: 'user',
		labelKey: 'following.labels.profileHistoryLogger.workerUpdated',
	},
	'mobile-app-registration': {
		icon: 'mobile-arrow-left',
		labelKey: 'following.labels.profileHistoryLogger.mobileAppRegistration',
	},
	'send-invitation': {
		icon: 'message-plus',
		labelKey: 'following.labels.profileHistoryLogger.sendInvitation',
	},
	worker_registration_relaunched: {
		icon: 'repeat-single',
		labelKey: 'following.labels.profileHistoryLogger.registrationRelaunched',
	},
};

const resolveProfileHistoryLoggerTypeConfig = (
	type: string | undefined
): { icon: string; labelKey: string } | undefined => {
	if (type == null || typeof type !== 'string') return undefined;
	const key = type.trim().toLowerCase();
	return PROFILE_HISTORY_LOGGER_TYPE_CONFIG[key];
};

const isMobileAppRegistrationProfileHistoryType = (
	type: string | undefined
): boolean => type?.trim().toLowerCase() === 'mobile-app-registration';

export const isProfileHistoryNoteType = (type: string | undefined): boolean =>
	type?.trim().toLowerCase() === 'note';

export const isInterviewReportProfileHistoryType = (
	type: string | undefined
): boolean => {
	const normalized = type?.trim().toLowerCase();
	return (
		normalized != null && PROFILE_HISTORY_INTERVIEW_REPORT_TYPES.has(normalized)
	);
};

export interface ProfileHistoryFollowingMappingOptions {
	onViewPendingProfileChanges?: (event: MouseEvent) => void;
	workerId?: string;
}

const getActorProfileLabel = (): string =>
	i18n.global.t('following.labels.actorProfile');

const getUserFromOtherAgencyLabel = (): string =>
	i18n.global.t('following.labels.userFromOtherAgency');

/**
 * Forges the display name from a collaborator id using the agency collaborators loaded in the auth store.
 * Returns the formatted name when found, the other-agency label when the id is present but unknown,
 * or an empty string when there is no id to resolve.
 */
const resolveNameFromCollaboratorId = (
	collaboratorId: string | null | undefined
): string => {
	const id = collaboratorId?.trim();
	if (!id) return '';
	const collaborator = useAuthStore().getCollaborator(id);
	return collaborator
		? formatFullName(collaborator)
		: getUserFromOtherAgencyLabel();
};

const resolveCollaboratorDisplayName = (
	item: ProfileHistoryItem,
	payload: ProfileHistoryLoggerPayload,
	workerId?: string
): string => {
	const trimmedWorkerId = workerId?.trim();
	if (trimmedWorkerId && item.collaboratorId?.trim() === trimmedWorkerId) {
		return i18n.global.t('following.labels.profile');
	}

	const fromNestedCollaborator = payload.collaborator?.name?.trim();
	if (fromNestedCollaborator) return fromNestedCollaborator;

	const fromPayloadString =
		typeof payload.collaboratorName === 'string'
			? payload.collaboratorName.trim()
			: '';
	if (fromPayloadString) return fromPayloadString;

	const fromRoot =
		typeof item.collaboratorName === 'string'
			? item.collaboratorName.trim()
			: '';
	if (fromRoot) return fromRoot;

	const fromCollaboratorId = resolveNameFromCollaboratorId(item.collaboratorId);
	if (fromCollaboratorId) return fromCollaboratorId;

	return getActorProfileLabel();
};

const formatDetailFromDateAndCollaborator = (
	isoDate: string,
	item: ProfileHistoryItem,
	payload: ProfileHistoryLoggerPayload,
	workerId?: string
): string => {
	const time = moment(isoDate).format('HH:mm');
	const name = resolveCollaboratorDisplayName(item, payload, workerId);
	return `${time} - ${name}`;
};

const NOTE_ID_FIELD_NAMES = [
	'noteId',
	'note_id',
	'eventId',
	'logId',
	'id',
	'_id',
	'uuid',
] as const;

const keysMatch = (a: string, b: string): boolean =>
	a.toLowerCase().replace(/_/g, '') === b.toLowerCase().replace(/_/g, '');

const getRecordValueByFieldName = (
	record: Record<string, unknown>,
	fieldName: string
): unknown => {
	for (const [key, value] of Object.entries(record)) {
		if (keysMatch(key, fieldName)) {
			return value;
		}
	}
	return undefined;
};

const noteIdFromUnknown = (value: unknown): string | undefined => {
	if (value == null) return undefined;
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return undefined;
};

/**
 * Resolves known id field names with case-insensitive keys (e.g. NoteId, NOTE_ID).
 */
const pickNoteIdFromRecord = (
	record: Record<string, unknown> | undefined
): string | undefined => {
	if (!record) return undefined;
	for (const fieldName of NOTE_ID_FIELD_NAMES) {
		const value = getRecordValueByFieldName(record, fieldName);
		const id = noteIdFromUnknown(value);
		if (id) return id;
	}
	return undefined;
};

/**
 * Matches noteId, note_id, NOTE_ID, etc. (avoids picking unrelated ids when only a note id is present).
 */
const pickNoteIdByNormalizedKey = (
	record: Record<string, unknown> | undefined
): string | undefined => {
	if (!record) return undefined;
	for (const [key, value] of Object.entries(record)) {
		const normalized = key.toLowerCase().replace(/_/g, '');
		if (normalized === 'noteid') {
			const id = noteIdFromUnknown(value);
			if (id) return id;
		}
	}
	return undefined;
};

const asObjectRecord = (
	value: unknown
): Record<string, unknown> | undefined => {
	if (value == null) return undefined;
	if (typeof value === 'string') {
		try {
			const parsed = JSON.parse(value) as unknown;
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>;
			}
		} catch {
			return undefined;
		}
		return undefined;
	}
	if (typeof value === 'object' && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return undefined;
};

const getLoggerPayloadRecord = (
	payload: ProfileHistoryItem['payload']
): ProfileHistoryLoggerPayload => {
	const record = asObjectRecord(payload) as
		| ProfileHistoryLoggerPayload
		| undefined;
	return record ?? {};
};

const pickDedicatedNoteIdField = (
	record: Record<string, unknown> | undefined
): string | undefined => {
	if (!record) return undefined;
	for (const fieldName of ['noteId', 'note_id'] as const) {
		const value = getRecordValueByFieldName(record, fieldName);
		const id = noteIdFromUnknown(value);
		if (id) return id;
	}
	return pickNoteIdByNormalizedKey(record);
};

/** Prefer explicit note ids; then other known id keys (payload before root to avoid a generic row `id` masking `payload.noteId`). */
const tryResolveNoteIdFromRecord = (
	record: Record<string, unknown> | undefined
): string | undefined => {
	if (!record) return undefined;
	const dedicated = pickDedicatedNoteIdField(record);
	if (dedicated) return dedicated;
	return pickNoteIdFromRecord(record) ?? pickNoteIdByNormalizedKey(record);
};

const NESTED_VALUE_KEYS = [
	'data',
	'value',
	'body',
	'attributes',
	'details',
	'event',
	'metadata',
	'payload',
];

const extractNoteContent = (item: ProfileHistoryItem): string | undefined => {
	const record = getLoggerPayloadRecord(item.payload);
	const raw = item as unknown as Record<string, unknown>;
	const fromPayload = record.content;
	if (fromPayload != null) {
		if (typeof fromPayload === 'string') {
			const trimmed = fromPayload.trim();
			if (trimmed.startsWith('{')) {
				try {
					const parsed = JSON.parse(trimmed) as Record<string, unknown>;
					if (typeof parsed.content === 'string' && parsed.content.trim()) {
						return parsed.content.trim();
					}
					const internalComment = (parsed as { internalComment?: string })
						.internalComment;
					if (typeof internalComment === 'string' && internalComment.trim()) {
						return internalComment.trim();
					}
				} catch {
					return trimmed || undefined;
				}
			}
			if (trimmed) return trimmed;
		}
		if (
			typeof fromPayload === 'object' &&
			fromPayload !== null &&
			!Array.isArray(fromPayload)
		) {
			const o = fromPayload as Record<string, unknown>;
			if (typeof o.content === 'string' && o.content.trim()) {
				return o.content.trim();
			}
			if ('internalComment' in o) {
				const comment = (
					o as { internalComment?: string }
				).internalComment?.trim();
				if (comment) return comment;
			}
		}
	}
	if (typeof raw.content === 'string') {
		const trimmed = raw.content.trim();
		if (trimmed) return trimmed;
	}
	return undefined;
};

/**
 * Resolves the worker note id for PATCH /workers/:id/notes/:noteId.
 * Legacy activity rows only expose `payload.content.internalComment` and omit `noteId`; the activity API must add
 * `noteId` (e.g. on `payload` or `payload.content`) for edit-from-feed to work.
 */
const resolveProfileHistoryNoteId = (
	item: ProfileHistoryItem
): string | undefined => {
	const raw = item as unknown as Record<string, unknown>;
	const payloadRecord = asObjectRecord(raw.payload);
	const dataRecord = asObjectRecord(raw.data);

	const searchNested = (
		parent: Record<string, unknown> | undefined
	): string | undefined => {
		if (!parent) return undefined;
		for (const key of NESTED_VALUE_KEYS) {
			const value = getRecordValueByFieldName(parent, key);
			const inner = asObjectRecord(value);
			const id = tryResolveNoteIdFromRecord(inner);
			if (id) return id;
		}
		return undefined;
	};

	const fromPayload = tryResolveNoteIdFromRecord(payloadRecord);
	if (fromPayload) return fromPayload;

	const contentField = payloadRecord?.content;
	if (
		contentField &&
		typeof contentField === 'object' &&
		!Array.isArray(contentField)
	) {
		const fromContent = tryResolveNoteIdFromRecord(
			contentField as Record<string, unknown>
		);
		if (fromContent) return fromContent;
	}
	if (typeof contentField === 'string') {
		const trimmed = contentField.trim();
		if (trimmed.startsWith('{')) {
			try {
				const parsed = JSON.parse(trimmed) as Record<string, unknown>;
				const fromJson = tryResolveNoteIdFromRecord(parsed);
				if (fromJson) return fromJson;
			} catch {
				/* not JSON */
			}
		}
	}

	const fromData = tryResolveNoteIdFromRecord(dataRecord);
	if (fromData) return fromData;

	const fromPayloadNested = searchNested(payloadRecord);
	if (fromPayloadNested) return fromPayloadNested;

	const fromDataNested = searchNested(dataRecord);
	if (fromDataNested) return fromDataNested;

	const metadataRecord = asObjectRecord(raw.metadata);
	const fromMeta = tryResolveNoteIdFromRecord(metadataRecord);
	if (fromMeta) return fromMeta;

	const fromTop = tryResolveNoteIdFromRecord(raw);
	if (fromTop) return fromTop;

	return undefined;
};

const buildProfileHistoryLine = (
	item: ProfileHistoryItem,
	resolvedNoteId: string | undefined,
	options?: ProfileHistoryFollowingMappingOptions
): FollowingLineGeneratorResult | null => {
	const dateMs = new Date(item.date).getTime();
	const payloadForDisplay = getLoggerPayloadRecord(item.payload);
	const isMobileCreation =
		normalizeProfileHistoryType(item.type) ===
		PROFILE_HISTORY_WORKER_CREATED_FROM_MOBILE_TYPE;
	const showTimeOnlyDetail =
		isMobileAppRegistrationProfileHistoryType(item.type) ||
		(isProfileHistoryCreationType(item.type) &&
			!hasResolvableCreationAuthor(item) &&
			!isMobileCreation);
	const detail = showTimeOnlyDetail
		? moment(item.date).format('HH:mm')
		: formatDetailFromDateAndCollaborator(
				item.date,
				item,
				payloadForDisplay,
				options?.workerId
			);

	if (isProfileHistoryCreationType(item.type)) {
		return {
			icon: PROFILE_HISTORY_CREATION_ICON,
			segments: [
				createReadonlyLabelSegment(
					resolveCreationLabel(item, payloadForDisplay as WorkerCreatedPayload)
				),
			],
			detail,
		};
	}

	if (isProfileHistoryNoteType(item.type)) {
		const noteIdForLine = resolvedNoteId ?? resolveProfileHistoryNoteId(item);
		return buildFollowingLineNote({
			date: dateMs,
			detail,
			content: extractNoteContent(item),
			noteId: noteIdForLine,
			logCollaboratorId: item.collaboratorId,
		});
	}

	if (isWorkerContactedProfileHistoryType(item.type)) {
		const contactPayload = payloadForDisplay as WorkerContactedPayload;
		const contactType = normalizeWorkerContactedType(
			contactPayload.contactType
		);
		if (!contactType) return null;
		return buildFollowingLinePriseDeContact({
			type: contactType,
			date: dateMs,
			detail,
			comment: contactPayload.comment,
		});
	}

	if (isInterviewReportProfileHistoryType(item.type)) {
		const interviewPayload =
			payloadForDisplay as ProfileHistoryLoggerPayload & {
				summary?: string;
				content?: string;
			};
		const hasSummary =
			typeof interviewPayload.summary === 'string' &&
			interviewPayload.summary.trim() !== '';
		const legacyContent =
			typeof interviewPayload.content === 'string'
				? interviewPayload.content
				: undefined;
		return buildFollowingLineInterviewReport({
			date: dateMs,
			detail,
			summary: hasSummary ? interviewPayload.summary : legacyContent,
		});
	}

	if (item.type === PROFILE_HISTORY_ADD_CV_TYPE) {
		return buildFollowingLineProfil({
			type: ProfilFollowingType.CV_UPDATED,
			date: dateMs,
			detail,
		});
	}

	if (item.type === PROFILE_HISTORY_DATA_UPDATED_TYPE) {
		const extendedPayload = payloadForDisplay as ProfileHistoryLoggerPayload & {
			pendingFields?: string[];
		};
		const summary = extendedPayload.pendingFields?.length
			? extendedPayload.pendingFields
			: undefined;
		return buildFollowingLineProfil({
			type: ProfilFollowingType.DATA_UPDATED,
			date: dateMs,
			detail,
			summary,
			onViewChanges: options?.onViewPendingProfileChanges,
		});
	}

	if (item.type?.trim().toLowerCase() === 'worker_deactivated') {
		const deactivatePayload =
			payloadForDisplay as ProfileHistoryLoggerPayload & {
				reasonId?: string;
			};
		const reasonId = deactivatePayload.reasonId;
		const { inactivatedReasonsMap } = useConfigurationStore();
		const reasonLabel = reasonId
			? inactivatedReasonsMap.get(reasonId)
			: undefined;

		return buildFollowingLineProfil({
			type: ProfilFollowingType.NON_DELEGABLE,
			date: dateMs,
			detail,
			labelSuffix: reasonLabel,
		});
	}

	if (item.type?.trim().toLowerCase() === 'worker_reactivated') {
		return buildFollowingLineProfil({
			type: ProfilFollowingType.DELEGABLE,
			date: dateMs,
			detail,
		});
	}

	const loggerTypeConfig = resolveProfileHistoryLoggerTypeConfig(item.type);
	if (loggerTypeConfig) {
		return {
			icon: loggerTypeConfig.icon,
			segments: [
				createReadonlyLabelSegment(i18n.global.t(loggerTypeConfig.labelKey)),
			],
			detail,
		};
	}

	return null;
};

const buildStableEntryId = (item: ProfileHistoryItem): string =>
	`profile-history-${item.date}-${item.type}-${item.collaboratorId ?? 'null'}`;

const toAgencyScalar = (value: unknown): string | undefined => {
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return undefined;
};

const resolveAgencyMetaFromProfileItem = (
	item: ProfileHistoryItem
): { agencyId?: string; agencyName?: string } => {
	const payload = item.payload;
	const payloadRecord =
		payload && typeof payload === 'object'
			? (payload as Record<string, unknown>)
			: undefined;

	const fromPayloadRootId = payloadRecord
		? toAgencyScalar(payloadRecord.agencyId ?? payloadRecord.agency_id)
		: undefined;
	if (fromPayloadRootId) {
		const nameFromPayload =
			toAgencyScalar(payloadRecord?.agencyName) ??
			toAgencyScalar(payloadRecord?.publicName);
		return {
			agencyId: fromPayloadRootId,
			agencyName: nameFromPayload,
		};
	}

	const fromItemRootId = toAgencyScalar(item.agencyId);
	if (fromItemRootId) {
		const extra = item as unknown as Record<string, unknown>;
		return {
			agencyId: fromItemRootId,
			agencyName: toAgencyScalar(extra.agencyName),
		};
	}

	const agency = item.payload?.agency;
	if (!agency || typeof agency !== 'object') return {};
	const agencyRecord = agency as unknown as Record<string, unknown>;
	const rawId = agencyRecord._id ?? agencyRecord.id;
	const rawName = agencyRecord.name;
	const idString = toAgencyScalar(rawId);
	const nameString = toAgencyScalar(rawName);
	return {
		agencyId: idString,
		agencyName: nameString,
	};
};

export const mapProfileHistoryItemToFollowingEvent = (
	item: ProfileHistoryItem,
	options?: ProfileHistoryFollowingMappingOptions
): FollowingEvent | null => {
	const resolvedNoteId = resolveProfileHistoryNoteId(item);
	const line = buildProfileHistoryLine(item, resolvedNoteId, options);
	if (!line) return null;

	const { agencyId, agencyName } = resolveAgencyMetaFromProfileItem(item);
	return {
		id: resolvedNoteId ?? buildStableEntryId(item),
		createdAt: new Date(item.date),
		line,
		agencyId,
		agencyName,
		source: WorkerActivitySource.PROFILE_HISTORY,
		profileHistoryType: item.type,
	};
};

export const workerProfileHistoryEntriesToFollowingEvents = (
	entries: ProfileHistoryItem[],
	options?: ProfileHistoryFollowingMappingOptions
): FollowingEvent[] =>
	entries
		.map((entry) => mapProfileHistoryItemToFollowingEvent(entry, options))
		.filter((event): event is FollowingEvent => event != null);
